import React, { useState } from "react";
import { Send } from "lucide-react";
import { submitToHubSpot } from "../../../services/hubspot";

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

interface FormStatus {
  type: "idle" | "loading" | "success" | "error";
  message?: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [status, setStatus] = useState<FormStatus>({ type: "idle" });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: "loading" });

    try {
      await submitToHubSpot(formData);

      setStatus({
        type: "success",
        message: "Thank you! Your message has been sent successfully.",
      });
      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
      setStatus({
        type: "error",
        message:
          "Failed to send message. Please try again or email us directly.",
      });
    }
  };

  return (
    <div className="bg-dark-bg min-h-screen">
      {/* Header */}
      <div className="container mx-auto w-full max-w-5xl px-5 md:px-0 pt-20">
        <h1 className="text-5xl md:text-[3.5rem] leading-none font-semibold">
          <span className="bg-gradient-text bg-clip-text text-transparent">
            Get in Touch
          </span>
        </h1>
        <p className="mt-4 text-white/60 text-lg max-w-xl leading-relaxed">
          Have questions about Data Revival? Want to see how we can help unlock
          the potential in your chemical archives?
        </p>
      </div>

      {/* Divider */}
      <div className="container mx-auto w-full max-w-5xl px-5 md:px-0 mt-12">
        <div className="border-t border-white/10" />
      </div>

      {/* Main content */}
      <div className="container mx-auto w-full max-w-5xl px-5 md:px-0 mt-16 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-16">
          {/* Left: contact info */}
          <div>
            <p className="text-xs uppercase tracking-widest text-white/40 mb-8">
              Contact
            </p>
            <div className="space-y-8">
              <div>
                <p className="text-xs uppercase tracking-widest text-white/40 mb-1.5">
                  Email
                </p>
                <a
                  href="mailto:contact@data-revival.com"
                  className="text-white text-sm hover:text-[#00A3FF] transition-colors"
                >
                  contact@data-revival.com
                </a>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-white/40 mb-1.5">
                  Phone
                </p>
                <p className="text-white text-sm">+44 (0) 123 456 7890</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-white/40 mb-1.5">
                  Location
                </p>
                <p className="text-white text-sm">
                  Southampton, United Kingdom
                </p>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div>
            {status.type === "success" && (
              <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
                {status.message}
              </div>
            )}

            {status.type === "error" && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <form className="space-y-5" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-xs uppercase tracking-widest text-white/40 mb-2"
                  >
                    Name <span className="text-[#00A3FF]">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    disabled={status.type === "loading"}
                    className="w-full px-4 py-3 bg-white/[0.03] text-white text-sm border border-white/10 rounded-lg focus:outline-none focus:border-[#00A3FF]/60 focus:bg-white/[0.05] transition-all placeholder:text-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-xs uppercase tracking-widest text-white/40 mb-2"
                  >
                    Email <span className="text-[#00A3FF]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    disabled={status.type === "loading"}
                    className="w-full px-4 py-3 bg-white/[0.03] text-white text-sm border border-white/10 rounded-lg focus:outline-none focus:border-[#00A3FF]/60 focus:bg-white/[0.05] transition-all placeholder:text-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="block text-xs uppercase tracking-widest text-white/40 mb-2"
                >
                  Company / Organisation
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  disabled={status.type === "loading"}
                  className="w-full px-4 py-3 bg-white/[0.03] text-white text-sm border border-white/10 rounded-lg focus:outline-none focus:border-[#00A3FF]/60 focus:bg-white/[0.05] transition-all placeholder:text-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Your company"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-xs uppercase tracking-widest text-white/40 mb-2"
                >
                  Message <span className="text-[#00A3FF]">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  disabled={status.type === "loading"}
                  className="w-full px-4 py-3 bg-white/[0.03] text-white text-sm border border-white/10 rounded-lg focus:outline-none focus:border-[#00A3FF]/60 focus:bg-white/[0.05] transition-all resize-none placeholder:text-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Tell us about your project or ask us a question..."
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={status.type === "loading"}
                  className="inline-flex items-center justify-center bg-gradient-to-r from-[#00A3FF] to-[#0077CC] hover:shadow-lg hover:shadow-[#00A3FF]/25 rounded-lg px-8 py-3 text-white text-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status.type === "loading" ? (
                    <>
                      Sending...{" "}
                      <Send className="ml-2 animate-pulse" size={16} />
                    </>
                  ) : (
                    <>
                      Send message <Send className="ml-2" size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
